import { ApiError, buildUrl, type QueryParams } from '../../api/client'

export interface DownloadResult {
  rows: number
  filename: string
}

function filenameOf(disposition: string | null, fallback: string): string {
  const match = /filename="([^"]+)"/.exec(disposition ?? '')
  return match?.[1] ?? fallback
}

/**
 * Download a server-side export (P-RPT-002): the server scopes, escapes, stores
 * and logs it; the browser only saves the file. Errors (422 too many rows, 429)
 * come back as the D-15 body and are thrown as `ApiError`.
 */
export async function downloadExport(path: string, query: QueryParams): Promise<DownloadResult> {
  const response = await fetch(buildUrl(path, query), {
    credentials: 'include',
    headers: { Accept: 'text/csv, application/json' },
  })
  if (!response.ok) {
    let code = 'unexpected_response'
    let message = `The export failed (HTTP ${response.status}).`
    let requestId: string | undefined
    try {
      const body = (await response.json()) as {
        error?: { code: string; message: string; request_id?: string }
      }
      if (body.error) {
        code = body.error.code
        message = body.error.message
        requestId = body.error.request_id
      }
    } catch {
      // keep the generic message
    }
    throw new ApiError({
      status: response.status,
      code,
      message,
      ...(requestId ? { requestId } : {}),
    })
  }
  const blob = await response.blob()
  const filename = filenameOf(response.headers.get('Content-Disposition'), 'fruuit-export.csv')
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return { rows: Number(response.headers.get('X-Export-Rows') ?? '1'), filename }
}
